import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import {
  DocumentQueryHelper,
  GenericQueryOptions,
} from '../../../../../core/services/document/helpers/document-helper';

export interface SubscriptionHistoryResponse {
  data: { data: SubscriptionItem[] };
  count: number;
  total: number;
  page: number;
  pageCount: number;
}

export interface SubscriptionItem {
  _id: string;
  status: string;
  planId: string;
  customerId: string;
  customerType: string;
  providerName: string;
  subscriptionId: string;
  createdAt: string;
  updatedAt: string;
  plan?: {
    name: string;
    price: number;
    billing_cycle: string;
    [key: string]: any;
  };
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  /**
   * HTTP options for the requests
   */
  private httpOptions = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }),
  };

  /**
   * URL for the API
   */
  private urlApi = environment.url_api;

  /**
   * Constructor for the SubcriptionService class
   * @param http HttpClient
   * @param documentQueryHelper DocumentQueryHelper
   */
  constructor(
    private http: HttpClient,
    private readonly documentQueryHelper: DocumentQueryHelper
  ) {}

  /** Get all plans
   * @returns An observable containing the plans data
   **/
  getPlans(): Observable<any> {
    return this.http.get(this.urlApi + 'plan?page=1&limit=5', this.httpOptions);
  }

  /** Create a subscription
   * @param userId - The ID of the user
   * @param planId - The ID of the plan
   * @param provider - Payment provider ('paypal' or 'openpay')
   * @param tokenId - OpenPay card token (required for OpenPay)
   * @param deviceSessionId - OpenPay device session ID (required for OpenPay)
   * @returns An observable containing the subscription data
   **/
  createSubscription(
    userId: string,
    planId: string,
    provider: 'paypal' | 'openpay' = 'paypal',
    tokenId?: string,
    deviceSessionId?: string,
    contact?: { firstName?: string; lastName?: string; phoneNumber?: string }
  ): Observable<any> {
    const payload: any = {
      customerId: userId,
      planId,
      customerType: 'User',
    };

    if (provider === 'openpay' && tokenId && deviceSessionId) {
      payload.tokenId = tokenId;
      payload.deviceSessionId = deviceSessionId;
      if (contact?.firstName) payload.firstName = contact.firstName;
      if (contact?.lastName) payload.lastName = contact.lastName;
      if (contact?.phoneNumber) payload.phoneNumber = contact.phoneNumber;
    }

    return this.http.post(
      `${this.urlApi}subscription?provider=${provider}`,
      payload,
      this.httpOptions
    );
  }

  /** Cancel a subscription
   * @param subscriptionId - The ID of the subscription
   * @param provider - Payment provider ('paypal' or 'openpay')
   * @returns An observable containing the subscription data
   **/
  cancelSubscription(
    subscriptionId: string,
    provider: 'paypal' | 'openpay' = 'paypal'
  ): Observable<any> {
    const payload = {
      subscriptionId,
      providerName: provider,
    };

    return this.http.post(this.urlApi + 'subscription/cancel', payload, this.httpOptions);
  }

  /**
   * Get subscriptions history with filtering and sorting capabilities
   * @param options Optional query options for filtering and sorting
   * @returns An observable containing the subscriptions history data
   **/
  getSubscriptionsHistory(options?: GenericQueryOptions): Observable<SubscriptionHistoryResponse> {
    const baseURI = this.urlApi + 'me/subscriptions';
    const queryString = options ? this.documentQueryHelper.buildGenericQueryString(options) : '';

    console.debug(`getSubscriptionsHistory → ${baseURI}${queryString}`);

    // Using the same pattern as other methods
    return this.http.get<SubscriptionHistoryResponse>(`${baseURI}${queryString}`, this.httpOptions);
  }

  /**
   * Get subscription by ID
   * @param subscriptionId - The ID of the subscription
   * @returns An observable containing the subscription data
   */
  getSubscription(subscriptionId: string): Observable<any> {
    return this.http.get(this.urlApi + 'subscription/' + subscriptionId, this.httpOptions);
  }

  /**
   * Schedule a subscription to cancel at the end of the current billing period.
   * The subscription remains active until the period ends.
   * @param subscriptionId - The ID of the subscription
   * @returns An observable containing the updated subscription data with deferred-cancel fields
   **/
  scheduleCancel(subscriptionId: string): Observable<any> {
    return this.http.post(
      `${this.urlApi}subscriptions/${subscriptionId}/schedule-cancel`,
      {},
      this.httpOptions
    );
  }

  /**
   * Reactivate a subscription that was scheduled to cancel at period end.
   * Only callable while canReactivate === true (before the period expires).
   * @param subscriptionId - The ID of the subscription
   * @returns An observable containing the updated subscription data
   **/
  reactivate(subscriptionId: string): Observable<any> {
    return this.http.post(
      `${this.urlApi}subscriptions/${subscriptionId}/reactivate`,
      {},
      this.httpOptions
    );
  }

  /** Update a subscription
   * @param subscriptionId - The ID of the subscription
   * @param planId - The ID of the new plan
   * @param userId - The ID of the user
   * @param provider - Payment provider ('paypal' or 'openpay')
   * @param tokenId - OpenPay card token (required for OpenPay)
   * @param deviceSessionId - OpenPay device session ID (required for OpenPay)
   * @returns An observable containing the updated subscription data
   **/
  updateSubscription(
    subscriptionId: string,
    planId: string,
    userId: string,
    provider: 'paypal' | 'openpay' = 'paypal',
    tokenId?: string,
    deviceSessionId?: string,
    contact?: { firstName?: string; lastName?: string; phoneNumber?: string }
  ): Observable<any> {
    const payload: any = {
      subscriptionId,
      planId,
      customerId: userId,
      customerType: 'User',
    };

    if (provider === 'openpay' && tokenId && deviceSessionId) {
      payload.tokenId = tokenId;
      payload.deviceSessionId = deviceSessionId;
      if (contact?.firstName) payload.firstName = contact.firstName;
      if (contact?.lastName) payload.lastName = contact.lastName;
      if (contact?.phoneNumber) payload.phoneNumber = contact.phoneNumber;
    }

    return this.http.patch(
      `${this.urlApi}subscription/${subscriptionId}?provider=${provider}`,
      payload,
      this.httpOptions
    );
  }
}
